class PlugHeader extends HTMLElement {
    constructor() {
        super();
    }

    connectedCallback() {
        this.elements = {
            headerBar: this.querySelector(".plug-header__bar"),
            imageDropdowns: Array.from(this.getElementsByClassName("plug-header__dropdown--images")),
            l1Links: Array.from(this.getElementsByClassName("plug-header__l1-link")),
        };

        this.removeListeners = this.initListeners();
        this.revertColorsTimer = undefined;
        this.closeTimer = undefined;
    }

    initListeners() {
        const onMouseEnterL1 = (event) => {
            const li = event.currentTarget;
            this.openDropdown(li);
        };

        const onMouseLeaveL1 = (event) => {
            const li = event.currentTarget;
            this.closeDropdown(li);
        };

        const onMouseEnterDropdown = (event) => {
            const dropdown = event.currentTarget;
            clearTimeout(this.closeTimer);
            clearTimeout(this.revertColorsTimer);
        };

        const onMouseLeaveDropdown = (event) => {
            const dropdown = event.currentTarget;
            const li = this.querySelector(`.plug-header__menu-item[data-index="${dropdown.dataset.index}"]`);
            this.closeDropdown(li);
        };

        this.elements.l1Links.forEach((link, l1LinkIndex) => {
            const li = link.closest("li");
            li.addEventListener("mouseenter", onMouseEnterL1, false);
            li.addEventListener("mouseleave", onMouseLeaveL1, false);

            link.addEventListener("keydown", (e) => {
                if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
                    if (e.key === "ArrowRight") {
                        if (l1LinkIndex < this.elements.l1Links.length - 1) {
                            e.preventDefault();
                            this.elements.l1Links[l1LinkIndex + 1].focus();
                        } else if (l1LinkIndex === this.elements.l1Links.length - 1) {
                            e.preventDefault();
                            this.elements.l1Links[0].focus(); // Loop to first link
                        }
                    } else if (e.key === "ArrowLeft") {
                        if (l1LinkIndex > 0) {
                            e.preventDefault();
                            this.elements.l1Links[l1LinkIndex - 1].focus();
                        } else if (l1LinkIndex === 0) {
                            e.preventDefault();
                            this.elements.l1Links[this.elements.l1Links.length - 1].focus(); // Loop to last link
                        }
                    }
                }
            });
        });

        this.elements.imageDropdowns.forEach(dropdown => {
            dropdown.addEventListener("mouseenter", onMouseEnterDropdown, false);
            dropdown.addEventListener("mouseleave", onMouseLeaveDropdown, false);
        });


        return () => {
            this.elements.l1Links.forEach(link => {
                link.removeEventListener("mouseenter", onMouseEnterL1, false);
                link.removeEventListener("mouseleave", onMouseLeaveL1, false);
            });
            this.elements.imageDropdowns.forEach(dropdown => {
                dropdown.removeEventListener("mouseenter", onMouseEnterDropdown, false);
                dropdown.removeEventListener("mouseleave", onMouseLeaveDropdown, false);
            });
        };
    }

    disconnectedCallback() {
        if (typeof this.removeListeners === "function") {
            this.removeListeners();
        }
    }

    invertHeaderColors() {
        clearTimeout(this.revertColorsTimer);
        this.classList.add("dropdown-is-open");
        this.elements.headerBar.classList.add("has-inverted-colors");
    }

    revertHeaderColors() {
        this.classList.remove("dropdown-is-open");
        this.elements.headerBar.classList.remove("has-inverted-colors");
    }

    openDropdown(li) {
        li.classList.add("is-active");

        this.elements.l1Links.forEach(link => {
            const linkL1 = link.closest("li");
            if (linkL1.dataset.index !== li.dataset.index) {
                this.closeDropdown(linkL1, true);
            }
        });

        if (li.classList.contains("has-dropdown")) {
            this.invertHeaderColors();
            li.querySelector(".plug-header__l1-link").setAttribute("aria-expanded", "true");

            const dropdown = (
                li.dataset.isImageDropdown !== "true"
                ? li.querySelector(".plug-header__dropdown")
                : this.querySelector(`.plug-header__dropdown--images[data-index="${li.dataset.index}"]`)
            );

            dropdown.classList.add("is-open");

            setTimeout(() => {
                if (li.classList.contains("is-active")) {
                    dropdown.classList.add("is-visible");
                }
            }, 300);
        }
    }

    closeDropdown(li, openingAnother = false) {
        const hasDropdown = li.classList.contains("has-dropdown");
        const hasImageDropdown = li.dataset.isImageDropdown === "true";

        const dropdown = (
            !hasDropdown
            ? undefined
            : (
                !hasImageDropdown
                ? li.querySelector(".plug-header__dropdown")
                : this.querySelector(`.plug-header__dropdown[data-index="${li.dataset.index}"]`)
            )
        );

        const close = () => {
            li.classList.remove("is-active");

            if (hasDropdown) {
                dropdown.classList.remove("is-open");
                dropdown.classList.remove("is-visible");
            }
        };

        if (hasDropdown) {
            li.querySelector(".plug-header__l1-link").setAttribute("aria-expanded", "false");
            if (!openingAnother) {
                clearTimeout(this.revertColorsTimer);
            }
        }

        if (openingAnother) {
            close();
        } else {
            if (hasImageDropdown) {
                this.closeTimer = setTimeout(close, 100);
                this.revertColorsTimer = setTimeout(() => this.revertHeaderColors(), 250);
            } else {
                close();
                if (hasDropdown) {
                    this.revertColorsTimer = setTimeout(() => this.revertHeaderColors(), 150);
                }
            }
        }
    }
}

if (!customElements.get("plug-header")) {
    customElements.define("plug-header", PlugHeader);
}

class PlugStickyHeader extends HTMLElement {
    constructor() {
        super();
    }

    connectedCallback() {
        this.headerSection = document.querySelector(".plug-header-section");
        this.headerBounds = {};

        this.setHeaderHeight();

        window.matchMedia("(max-width: 1400px)").addEventListener("change", this.setHeaderHeight.bind(this));

        this.currentScrollTop = 0;
        this.preventReveal = false;
        this.preventHide = false;

        this.onScrollHandler = this.onScroll.bind(this);
        this.hideHeaderOnScrollUp = () => this.preventReveal = true;

        this.addEventListener("preventHeaderReveal", this.hideHeaderOnScrollUp);
        window.addEventListener("scroll", this.onScrollHandler, false);

        this.createObserver();
    }

    setHeaderHeight() {
        this.preventReveal = true;
        document.documentElement.style.setProperty("--plug-header-height", `${this.headerSection.offsetHeight}px`);
        requestAnimationFrame(() => {
            setTimeout(() => {
                this.preventReveal = false;
            }, 100);
        });
    }

    disconnectedCallback() {
        this.removeEventListener("preventHeaderReveal", this.hideHeaderOnScrollUp);
        window.removeEventListener("scroll", this.onScrollHandler);
    }

    createObserver() {
        let observer = new IntersectionObserver((entries, observer) => {
            this.headerBounds = entries[0].intersectionRect;
            observer.disconnect();
        });

        observer.observe(this.headerSection);
    }

    onScroll() {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

        if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
            this.headerSection.classList.add("scrolled-past-header");
            if (this.preventHide) return;
            requestAnimationFrame(this.hide.bind(this));
        } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
            this.headerSection.classList.add("scrolled-past-header");
            if (!this.preventReveal) {
                requestAnimationFrame(this.reveal.bind(this));
            } else {
                window.clearTimeout(this.isScrolling);

                this.isScrolling = setTimeout(() => {
                    this.preventReveal = false;
                }, 66);

                requestAnimationFrame(this.hide.bind(this));
            }
        } else if (scrollTop <= this.headerBounds.top) {
            this.headerSection.classList.remove("scrolled-past-header");
            requestAnimationFrame(this.reset.bind(this));
        }

        this.currentScrollTop = scrollTop;
    }

    hide() {
        this.headerSection.classList.add("plug-header-section-hidden", "plug-header-section-sticky");
        this.closeMenuDisclosure();
        this.closeSearchModal();

    }

    reveal() {
        this.preventHide = true;
        this.headerSection.classList.add("plug-header-section-sticky", "animate");
        this.headerSection.classList.remove("plug-header-section-hidden");
        requestAnimationFrame(() => {
            setTimeout(() => {
                this.preventHide = false;
            }, 0);
        });
    }

    reset() {
        this.headerSection.classList.remove("plug-header-section-hidden", "plug-header-section-sticky", "animate");
    }

    closeMenuDisclosure() {
        // @todo
    }

    closeSearchModal() {
        // @todo
    }
}

if (!customElements.get("plug-sticky-header")) {
    customElements.define("plug-sticky-header", PlugStickyHeader);
}